import { DOCUMENT, Injectable, inject } from '@angular/core';
import { GbrUser, LanguageCode, Roles } from '@gbr-monorepo/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of } from 'rxjs';
import { UserManagementService } from '../../user-management/services/user-management.service';
import { SetUser, SetUserLanguage } from './user.action';

export interface UserStateModel {
    user?: GbrUser;
    isAuthenticated: boolean;
    language: LanguageCode | undefined;
}

@State<UserStateModel>({
    name: 'userState',
    defaults: {
        user: undefined,
        isAuthenticated: false,
        language: undefined
    }
})
@Injectable()
export class UserState {
    private userService = inject(UserManagementService);
    private document = inject<Document>(DOCUMENT);
    private translate = inject(TranslateService);

    @Selector()
    static user(state: UserStateModel): GbrUser | undefined {
        return state?.user;
    }

    @Selector()
    static isAdmin(state: UserStateModel): boolean {
        return state?.user?.roles?.includes(Roles.ADMIN) ?? false;
    }

    @Selector()
    static isProcessManager(state: UserStateModel): boolean {
        return state?.user?.roles?.includes(Roles.PROCESS_MANAGER) ?? false;
    }

    @Selector()
    static userLanguage(state: UserStateModel): LanguageCode {
        return state.language ? state.language : LanguageCode.EN;
    }

    @Action(SetUser)
    public setUser({ patchState }: StateContext<UserStateModel>, { payload }: SetUser) {
        return of(patchState({ user: payload.user, isAuthenticated: payload.isAuthenticated }));
    }

    @Action(SetUserLanguage)
    public setUserLanguage({ getState, patchState }: StateContext<UserStateModel>, { payload }: SetUserLanguage) {
        const { language: currentLanguage, user } = getState();
        const { gid } = user;
        const { language } = payload;
        this.translate.use(language);
        this.document.documentElement.lang = language;
        patchState({ language });
        if (currentLanguage && currentLanguage !== language) {
            return this.userService.updateUser({ gid, language });
        }
        return of(null);
    }
}
